// Small reusable widgets used by every sub-page. All visual primitives in one
// place so a designer can audit color/typography consistency at a glance.
import { formatNum } from '../../utils/format.js';
import { ageString, prettySource, statusTone } from './helpers.js';

// ─── KPI card (large stat with optional click-through) ──────────────────
export function KpiCard({ label, value, suffix = '', sub, tone = 'navy', onClick, cta }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      className={`db-kpi tone-${tone}${clickable ? ' is-clickable' : ''}`}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      } : undefined}
    >
      <div className="db-kpi-label">{label}</div>
      <div className="db-kpi-value">{formatNum(value, suffix)}</div>
      {sub && <div className="db-kpi-sub">{sub}</div>}
      {clickable && cta && <div className="db-kpi-cta">{cta} →</div>}
    </div>
  );
}

export function KpiGrid({ children }) {
  return <div className="db-kpi-grid">{children}</div>;
}

// ─── Status pill ─────────────────────────────────────────────────────────
export function StatusPill({ tone = 'gray', children }) {
  return <span className={`db-pill tone-${tone}`}>{children}</span>;
}

// ─── Tile (Overview drill-in) ───────────────────────────────────────────
export function Tile({ tone = 'navy', icon, label, sub, meta, onClick }) {
  return (
    <button type="button" className={`db-tile tone-${tone}`} onClick={onClick}>
      {icon && <div className="db-tile-icon">{icon}</div>}
      <div className="db-tile-label">{label}</div>
      {sub && <div className="db-tile-sub">{sub}</div>}
      {meta && <div className="db-tile-meta">{meta} →</div>}
    </button>
  );
}

export function TileGrid({ children }) {
  return <div className="db-tile-grid">{children}</div>;
}

// ─── Smart narrative card ───────────────────────────────────────────────
export function SmartText({ title = 'In plain English', lines }) {
  if (!lines || lines.length === 0) return null;
  return (
    <div className="db-smart">
      <div className="db-smart-head"><span className="db-smart-icon">i</span> {title}</div>
      <ul className="db-smart-list">
        {lines.map((l, i) => <li key={i}>{l}</li>)}
      </ul>
    </div>
  );
}

// ─── Banner (open alerts / failed pipeline) ─────────────────────────────
export function Banner({ tone = 'orange', title, children, action }) {
  return (
    <div className={`db-banner tone-${tone}`}>
      <span className="db-banner-icon">!</span>
      <div className="db-banner-text">
        <strong>{title}</strong>
        {children && <span> — {children}</span>}
      </div>
      {action}
    </div>
  );
}

// ─── Pipeline card ──────────────────────────────────────────────────────
export function PipelineCard({ item }) {
  const tone = statusTone(item.status);
  const ts = item.completed_at || item.started_at;
  return (
    <div className={`db-pipe tone-${tone}`}>
      <div className="db-pipe-head">
        <span className={`db-pipe-dot tone-${tone}`} />
        <span className="db-pipe-name">{item.label}</span>
      </div>
      <div className="db-pipe-meta">
        <StatusPill tone={tone}>
          {item.status === 'completed' ? 'Healthy' :
           item.status === 'failed'    ? 'Failed'  :
           item.status === 'running'   ? 'Running' :
           'No data'}
        </StatusPill>
        <span>Last run {ageString(ts)}</span>
        {item.records_extracted != null && (
          <span>{(item.records_extracted || 0).toLocaleString()} records</span>
        )}
      </div>
      {item.status === 'failed' && item.error_message && (
        <div className="db-pipe-error" title={item.error_message}>
          {item.error_message.length > 200 ? item.error_message.slice(0, 200) + '…' : item.error_message}
        </div>
      )}
    </div>
  );
}

// ─── Activity feed ──────────────────────────────────────────────────────
export function ActivityFeed({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="db-empty-sub">No recent extraction activity.</div>;
  }
  return (
    <ul className="db-activity">
      {rows.map((r, i) => {
        const tone = statusTone(r.status);
        const ts = r.completed_at || r.started_at;
        return (
          <li key={i}>
            <span className={`db-activity-dot tone-${tone}`} />
            <span className="db-activity-name">{prettySource(r.source)}</span>
            <StatusPill tone={tone}>{r.status}</StatusPill>
            {r.records_extracted != null && (
              <span className="db-activity-meta">{r.records_extracted.toLocaleString()} rec</span>
            )}
            <span className="db-activity-meta">{ageString(ts)}</span>
          </li>
        );
      })}
    </ul>
  );
}

// ─── DQ Gauge (semi-circular) ───────────────────────────────────────────
export function DqGauge({ score, label, tone, detail }) {
  const W = 180, H = 110, cx = W / 2, cy = H - 10, r = 70, stroke = 14;
  // Map 0..100 → -90deg..+90deg. Compute end-point of the score arc.
  const angleAt = (v) => Math.PI * (v / 100) - Math.PI; // -π..0
  const point = (a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  const [bx, by] = point(angleAt(0));
  const [ex, ey] = point(angleAt(score));
  const arc = `M ${bx} ${by} A ${r} ${r} 0 0 1 ${ex} ${ey}`;
  const bgArc = `M ${bx} ${by} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  const color =
    tone === 'green' ? 'var(--db-green)' :
    tone === 'orange' ? 'var(--db-orange)' :
    tone === 'red' ? 'var(--db-red)' :
    'var(--db-gray)';
  return (
    <div className={`db-gauge-card tone-${tone}`}>
      <svg viewBox={`0 0 ${W} ${H}`} className="db-gauge-svg" role="img" aria-label={`Data quality score ${score}`}>
        <path d={bgArc} stroke="var(--db-border)" strokeWidth={stroke} fill="none" strokeLinecap="round" />
        <path d={arc}   stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round" />
        <text x={cx} y={cy - 14} textAnchor="middle" fontSize="32" fontWeight="700" fill={color}>{score}</text>
        <text x={cx} y={cy + 4} textAnchor="middle" fontSize="10" fill="var(--db-text-muted)">/ 100</text>
      </svg>
      <div>
        <div className="db-gauge-label">Data quality</div>
        <div className={`db-gauge-band tone-${tone}`}>{label}</div>
        {detail && <div className="db-gauge-detail">{detail}</div>}
      </div>
    </div>
  );
}

// ─── Stacked bar chart (daily trend) ────────────────────────────────────
const W = 1400, H = 220, PAD_L = 50, PAD_R = 16, PAD_T = 16, PAD_B = 38;

export function BarChart({ rows, series, xKey = 'date', yMaxKey = 'total' }) {
  if (!rows || rows.length === 0) {
    return <div className="db-empty-sub">No history yet.</div>;
  }
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const slot = innerW / rows.length;
  const barW = Math.max(8, slot * 0.7);
  const max = Math.max(1, ...rows.map((d) => d[yMaxKey] || 0));
  const ticks = niceTicks(max, 4);
  const niceMax = Math.max(max, ticks[ticks.length - 1]);
  const yToPx = (v) => PAD_T + innerH - (v / niceMax) * innerH;

  const labelIdx = new Set([0, Math.floor(rows.length / 3), Math.floor(2 * rows.length / 3), rows.length - 1]);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="db-chart-svg" role="img" aria-label="Daily trend">
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={PAD_L} x2={W - PAD_R} y1={yToPx(t)} y2={yToPx(t)} className="db-chart-grid" />
          <text x={PAD_L - 8} y={yToPx(t) + 4} textAnchor="end" className="db-chart-y-label">{compact(t)}</text>
        </g>
      ))}
      {rows.map((d, i) => {
        const x = PAD_L + slot * i + (slot - barW) / 2;
        let bottom = PAD_T + innerH;
        const segs = series.map((s) => {
          const v = d[s.key] || 0;
          if (v === 0) return null;
          const h = (v / niceMax) * innerH;
          const y = bottom - h;
          const seg = <rect key={s.key} x={x} y={y} width={barW} height={h} fill={s.color} />;
          bottom = y;
          return seg;
        });
        const tipText = `${d[xKey]}\n` +
          series.map((s) => `${s.label}: ${(d[s.key] || 0).toLocaleString()}`).join(' · ');
        return (
          <g key={i}>
            {segs}
            <rect x={x - (slot - barW) / 2} y={PAD_T} width={slot} height={innerH} fill="transparent" pointerEvents="all">
              <title>{tipText}</title>
            </rect>
            {labelIdx.has(i) && d[xKey] && (
              <text x={x + barW / 2} y={H - 14} textAnchor="middle" className="db-chart-x-label">
                {shortDateLabel(d[xKey])}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── Sparkline (mini chart) ─────────────────────────────────────────────
export function Sparkline({ values, color = 'var(--db-navy)' }) {
  if (!values || values.length < 2) return <svg className="db-spark" />;
  const W = 200, H = 40, PAD = 2;
  const max = Math.max(1, ...values);
  const step = (W - 2 * PAD) / (values.length - 1);
  const yToPx = (v) => PAD + (H - 2 * PAD) * (1 - v / max);
  const d = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${PAD + i * step} ${yToPx(v)}`).join(' ');
  const lastX = PAD + (values.length - 1) * step;
  const lastY = yToPx(values[values.length - 1]);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="db-spark">
      <path d={d} stroke={color} strokeWidth="2" fill="none" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r="3" fill={color} />
    </svg>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────
function shortDateLabel(iso) {
  if (!iso) return '';
  const [, mm, dd] = iso.split('-');
  return `${parseInt(mm, 10)}/${parseInt(dd, 10)}`;
}
function compact(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}
function niceTicks(max, count) {
  if (max <= 0) return [0];
  const step = niceStep(max / count);
  const top = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = 0; v <= top + 0.5; v += step) ticks.push(Math.round(v));
  return ticks;
}
function niceStep(raw) {
  const exp = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / exp;
  let step;
  if (norm < 1.5)      step = 1;
  else if (norm < 3)   step = 2;
  else if (norm < 7)   step = 5;
  else                 step = 10;
  return step * exp;
}
